// 資訊頁的版面積木 — 對照 Unity `GameRuleInfo.prefab`（遊戲專屬段落）與
// `SlotFramework/Prefab/UI/Base/InfoCommonView.prefab`（共用段落）的節點樣式。
//
// Unity 兩份 prefab 的排版方式一致：根節點是 VerticalLayoutGroup（padding 上下 40、spacing 40、
// childAlignment UpperCenter）＋ ContentSizeFitter(Vertical=Preferred)，內容寬 1000 設計 px。
// 本移植不重造 UGUI 佈局系統，改以「積木清單依序排 y」還原同樣結果：每個 InfoBlock 自己排版並回報高度。
//
// ⚠️ 兩份 prefab 的標題漸層**不同**（不是同一組色）：
//   GameRuleInfo   內嵌 m_fontColorGradient  #ED6B45 → #EDDE60
//   InfoCommonView preset Color_Info_Title    #FF6432 → #FFFF80
// 內文一律 fontSize 28、純白（`m_enableVertexGradient: 0`，序列化裡雖有漸層值但關閉）。
//
// ⚠️ 所有文字都必須開 `breakWords`：PixiJS 的自動換行只在空白處斷詞，中文/日文/泰文沒有空白，
//    整段會被當成單一「詞」而完全不折、直接溢出框外（Unity TMP 對 CJK 本來就逐字斷行）。
//    來源字串多數自帶 `\n` 預先斷行，所以只有少數過長的單行會露出這個問題——很容易漏看。
//
// [已知差異] 斷行「位置」與 TMP 不完全相同：PixiJS 遇到放不下的詞會直接換行，只有「單一詞比整行還寬」
//    才逐字切，不會回頭把上一行填滿；TMP 則把 CJK 每個字都當斷點、填到接近行寬才斷。
//    因此夾著半形數字的長句（如 Info_Symbols_3 的「…獲得 3 個分散圖騰…」）會在空白處提早斷行，
//    第一行看起來偏短。內容完整、不溢出，僅斷點位置不同；要完全對齊得自己重寫一套 CJK 斷行器，
//    代價遠大於收益（插 U+200B 的取巧作法會有缺字方框風險，13 語系不划算）。

import { Container, FillGradient, Graphics, Sprite, Text, type Texture, type TextStyleOptions } from 'pixi.js'
import { fontFamilyForLocale } from '../../../../framework/i18n/i18n'
import { BitmapNumber } from '../../BitmapNumber'

/** 內容欄寬（Unity InfoPanel/Content 的 sizeDelta.x）。 */
export const CONTENT_W = 1000
/** 根 VerticalLayoutGroup 的 spacing 與上下 padding。 */
export const ROOT_SPACING = 40
export const ROOT_PADDING = 40
/** 標題：fontSize 62.65、Bold、置中、節點固定高 70。 */
const TITLE_FONT = 62.65
export const TITLE_H = 70
/** 內文：fontSize 28、純白。 */
export const BODY_FONT = 28

/**
 * 操作說明列（InfoCommonView 的 `Layout_*`）的度量，全部取自 prefab 序列化值：
 *   ICON_COLUMN_W  標題/說明 TMP 的 `m_margin.x`（左內縮讓出圖示欄）
 *   ROW_GAP        列內「標題→說明」「說明→說明」的間距（由 desc2 中心 y 反推：31.29+5+31.29+5+15.645=88.225，與序列化值相同）
 *   BULLET_*       說明列前方的白色小方塊（`m_Sprite` 為空＝Unity 內建白圖），aPos (130,−15)、pivot (0,0.5)
 */
const ICON_COLUMN_W = 150
const ROW_GAP = 5
const BULLET_SIZE = 10
const BULLET_X = 130
const BULLET_CY = 15

/** GameRuleInfo 標題漸層（內嵌 m_fontColorGradient）。 */
export const GAME_TITLE_GRADIENT = [0xed6b45, 0xedde60] as const
/** InfoCommonView 標題漸層（TMP preset Color_Info_Title）。 */
export const COMMON_TITLE_GRADIENT = [0xff6432, 0xffff80] as const

/** 一塊內容：自行排版並回報高度（螢幕 px）。 */
export interface InfoBlock {
  readonly node: Container
  /** s=設計→螢幕縮放、centerX=內容欄中心、top=本塊頂端 y；回傳本塊高度。 */
  layout(s: number, centerX: number, top: number): number
}

function titleStyle(fontSize: number, gradient: readonly [number, number]): TextStyleOptions {
  return {
    fontFamily: fontFamilyForLocale(),
    fontSize,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    breakWords: true,
    fill: new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: gradient[0] },
        { offset: 1, color: gradient[1] },
      ],
      textureSpace: 'local',
    }),
  }
}

/** 大標題（置中、垂直漸層、固定高 70 設計 px）。 */
export function titleBlock(text: string, gradient: readonly [number, number]): InfoBlock {
  const node = new Text({ text, style: titleStyle(TITLE_FONT, gradient) })
  node.anchor.set(0.5, 0)
  return {
    node,
    layout(s, centerX, top) {
      node.style = titleStyle(TITLE_FONT * s, gradient)
      node.style.wordWrapWidth = CONTENT_W * s
      node.position.set(centerX, top)
      return Math.max(TITLE_H * s, node.height)
    },
  }
}

/**
 * 內文段落。
 * width：Unity 該節點的 sizeDelta.x（各段不同，部分甚至寬於 1000 欄寬——ChildControlWidth=0 故不被壓縮）。
 * align：GameRuleInfo 的內文為置中（H=2）、InfoCommonView 為靠左（H=1）。
 * indent：InfoCommonView 的說明列以 `m_margin.x = 150` 左內縮讓出圖示欄。
 */
export function bodyBlock(
  text: string,
  width: number,
  align: 'center' | 'left' = 'center',
  indent = 0,
): InfoBlock {
  const node = new Text({
    text,
    style: {
      fontFamily: fontFamilyForLocale(),
      fontSize: BODY_FONT,
      fill: 0xffffff,
      align,
      wordWrap: true,
      breakWords: true,
    },
  })
  node.anchor.set(align === 'center' ? 0.5 : 0, 0)
  return {
    node,
    layout(s, centerX, top) {
      node.style.fontSize = BODY_FONT * s
      node.style.wordWrapWidth = (width - indent) * s
      const x = align === 'center' ? centerX : centerX - (CONTENT_W / 2 - indent) * s
      node.position.set(x, top)
      return node.height
    },
  }
}

/** 單張置中圖（Unity 以 sizeDelta 指定顯示尺寸，m_PreserveAspect 皆為 0 ⇒ 直接拉伸到該尺寸）。 */
export function imageBlock(texture: Texture, width: number, height: number): InfoBlock {
  const node = new Sprite(texture)
  node.anchor.set(0.5, 0)
  return {
    node,
    layout(s, centerX, top) {
      node.setSize(width * s, height * s)
      node.position.set(centerX, top)
      return height * s
    },
  }
}

/** 一排並列的圖（對應 Unity HorizontalLayoutGroup，MiddleCenter＋spacing）。 */
export function imageRowBlock(
  items: readonly { readonly texture: Texture; readonly width: number; readonly height: number }[],
  spacing: number,
): InfoBlock {
  const node = new Container()
  const sprites = items.map((item) => {
    const sprite = new Sprite(item.texture)
    sprite.anchor.set(0.5, 0)
    node.addChild(sprite)
    return sprite
  })
  const rowHeight = items.reduce((max, item) => Math.max(max, item.height), 0)
  return {
    node,
    layout(s, centerX, top) {
      const totalW = items.reduce((sum, item) => sum + item.width, 0) + spacing * (items.length - 1)
      let x = centerX - (totalW * s) / 2
      sprites.forEach((sprite, index) => {
        const item = items[index]!
        sprite.setSize(item.width * s, item.height * s)
        // 垂直 MiddleCenter：各圖在列高內置中。
        sprite.position.set(x + (item.width * s) / 2, top + ((rowHeight - item.height) * s) / 2)
        x += (item.width + spacing) * s
      })
      return rowHeight * s
    },
  }
}

/**
 * 疊在圖上的倍率字（Unity `MultiplierText`，資訊頁共 3 處：X10 fontSize 70、X3／X5 fontSize 50）。
 *
 * ⚠️ **不是一般文字**：這些 TMP 的 `m_fontAsset` 是 `Empty.asset`（`m_GlyphTable`／`m_CharacterTable`
 * 皆為空、0 個 glyph），字面完全來自 `m_spriteAsset: tx_Multiplier` 精靈圖集，畫面上呈現的是圖集本身的
 * 美術（白→黃→橘漸層字面＋深紅描邊）；`m_tintAllSprites: 0` ⇒ `m_fontColor`（純白）對精靈不生效。
 * 先前以一般 Text（純白系統字）繪製，字面與 Unity 完全不同。
 *
 * 逐欄位核對 `GameRuleInfo.prefab` 與 `LandscapeUI.prefab` 後確認：資訊頁這 3 處與遊戲中的倍率吊牌
 * （`MultiplierInfo/MultiplierText`，fontSize 70）／收集面板（`Level_1_Info`／`Level2_Info`，fontSize 50）
 * 的 fontAsset／spriteAsset／fontSize／對齊（Center+Middle）／字色／字距全部相同，故共用同一套位圖字。
 *
 * 字高換算沿用專案既有慣例「位圖字高 = TMP fontSize × s」（同 MultiplierView／CollectorView），
 * 讓說明頁與遊戲畫面看起來一致。
 * [已知差異] 嚴格的 TMP 算式是「sprite 高正規化到 fontAsset.ascentLine」＝`fontSize × 64.274414/71`
 * （tx_Multiplier.asset 的 `m_FaceInfo.m_PointSize` 為 0 ⇒ 走這條，同 tx_loading 的既有結論），
 * 故三處都比 Unity 大約 10.5%。要修就得三處一起改，不在本輪範圍。
 */
function multiplierLabel(caption: string): BitmapNumber {
  const label = new BitmapNumber('multiplier')
  label.setText(caption)
  return label
}

/**
 * 帶疊字的圖（Unity `FreeGameRuleImage_1`：容器 400×280，內含置中 Image 280×280
 * 與 `MultiplierText`（aPos (0,−27.3)、fontSize 70）疊在圖上）。
 * 容器寬度不影響排版（內容欄一律置中），故只取容器高度 `boxH` 佔位。
 */
export function captionedImageBlock(
  texture: Texture,
  boxH: number,
  imageW: number,
  imageH: number,
  caption: string,
  captionFont: number,
  captionOffsetY: number,
): InfoBlock {
  const node = new Container()
  const image = new Sprite(texture)
  image.anchor.set(0.5)
  // BitmapNumber 自身以 pivot 置中（等同 anchor 0.5）。
  const label = multiplierLabel(caption)
  node.addChild(image, label)
  return {
    node,
    layout(s, centerX, top) {
      const boxCY = top + (boxH * s) / 2
      image.setSize(imageW * s, imageH * s)
      image.position.set(centerX, boxCY)
      label.setHeight(captionFont * s)
      // Unity y 向上為正，故 aPos.y 取負值即為畫面下移量。
      label.position.set(centerX, boxCY - captionOffsetY * s)
      return boxH * s
    },
  }
}

/** `levelBarBlock` 的一個絕對定位子項（座標為相對容器中心的 Unity aPos，y 向上）。 */
export interface LevelBarItem {
  readonly texture?: Texture
  readonly ax: number
  readonly ay: number
  readonly width: number
  readonly height: number
  /** 疊在此項上的倍率字（如 X3／X5）；以 tx_Multiplier 位圖字繪製，見 `multiplierLabel`。 */
  readonly caption?: string
  /** 對應 Unity `MultiplierText` 的 `m_fontSize`（設計 px）。 */
  readonly captionFont?: number
  /** 疊在此項上的子圖（如 +3 旋轉字圖），座標相對本項中心。 */
  readonly overlay?: { readonly texture: Texture; readonly ax: number; readonly ay: number; readonly width: number; readonly height: number }
}

/**
 * 絕對定位的說明用小面板（Unity `FreeGameRuleImage_2` → `FreeSpinIngo`，884×100）。
 * 子節點 anchor/pivot 皆 0.5，故直接以「相對容器中心的偏移」定位。
 * 這是靜態說明圖，與遊戲中的 CollectorView 無狀態關聯。
 */
export function levelBarBlock(
  background: Texture,
  boxW: number,
  boxH: number,
  items: readonly LevelBarItem[],
): InfoBlock {
  const node = new Container()
  const bg = new Sprite(background)
  bg.anchor.set(0.5)
  node.addChild(bg)

  const parts = items.map((item) => {
    const sprite = item.texture !== undefined ? new Sprite(item.texture) : null
    if (sprite !== null) {
      sprite.anchor.set(0.5)
      node.addChild(sprite)
    }
    const label = item.caption !== undefined ? multiplierLabel(item.caption) : null
    if (label !== null) node.addChild(label)
    const overlay = item.overlay !== undefined ? new Sprite(item.overlay.texture) : null
    if (overlay !== null) {
      overlay.anchor.set(0.5)
      node.addChild(overlay)
    }
    return { item, sprite, label, overlay }
  })

  return {
    node,
    layout(s, centerX, top) {
      const boxCY = top + (boxH * s) / 2
      bg.setSize(boxW * s, boxH * s)
      bg.position.set(centerX, boxCY)
      for (const { item, sprite, label, overlay } of parts) {
        const cx = centerX + item.ax * s
        const cy = boxCY - item.ay * s
        if (sprite !== null) {
          sprite.setSize(item.width * s, item.height * s)
          sprite.position.set(cx, cy)
        }
        if (label !== null) {
          label.setHeight((item.captionFont ?? 50) * s)
          label.position.set(cx, cy)
        }
        if (overlay !== null && item.overlay !== undefined) {
          overlay.setSize(item.overlay.width * s, item.overlay.height * s)
          overlay.position.set(cx + item.overlay.ax * s, cy - item.overlay.ay * s)
        }
      }
      return boxH * s
    },
  }
}

/** 操作說明列左緣的圖示（Unity 是標題 TMP 的子節點，anchor/pivot 左上）。 */
export interface RowIcon {
  readonly texture: Texture
  readonly width: number
  readonly height: number
  /** prefab 的 aPos；y 在 Unity 為負值代表往下，這裡已換算成「往下為正」。 */
  readonly offsetX?: number
  readonly offsetY?: number
  /** prefab 的 localScale（Close 列的 btn_circle 是 0.6）。 */
  readonly scale?: number
  /** 疊在圖示中央的子圖（Close 列的 btn_close 疊在 btn_circle 上，隨父節點一起縮放）。 */
  readonly overlay?: { readonly texture: Texture; readonly width: number; readonly height: number }
}

/**
 * 操作說明列（Unity InfoCommonView 的 `Layout_*`）：左緣圖示 ＋ 右側標題與數行說明。
 *
 * Unity 結構：標題／說明都是寬 1000 的 TMP，以 `m_margin.x = 150` 左內縮讓出圖示欄；
 * 圖示是標題 TMP 的子節點（anchor/pivot 左上、aPos (0,0)）＝**絕對定位、不佔佈局高度**；
 * 每個說明列另有一個 10×10 白色方塊項目符號（aPos (130,−15)、pivot (0,0.5)，`m_Sprite` 為空＝內建白方塊）。
 * 列與列的間距 5 由 prefab 的 y 值反推：`Layout_Rules` 的 desc2 中心 = 31.29+5+31.29+5+15.645 = 88.225，與序列化值相同。
 *
 * 列高只計文字（照 Unity），圖示可能延伸進段落 spacing 內；因最短內容（標題＋單行說明）已有 67.58，
 * 加上根節點 spacing 40 仍大於最高的圖示 100，不會與下一列重疊。
 */
export function iconRowBlock(icon: RowIcon, title: string, descriptions: readonly string[]): InfoBlock {
  const iconScale = icon.scale ?? 1
  const iconOffsetX = icon.offsetX ?? 0
  const iconOffsetY = icon.offsetY ?? 0
  const iconW = icon.width * iconScale
  const iconH = icon.height * iconScale

  const node = new Container()
  const iconSprite = new Sprite(icon.texture)
  iconSprite.anchor.set(0, 0)
  const iconOverlay = icon.overlay !== undefined ? new Sprite(icon.overlay.texture) : null
  if (iconOverlay !== null) iconOverlay.anchor.set(0.5)
  const titleText = new Text({
    text: title,
    style: { fontFamily: fontFamilyForLocale(), fontSize: BODY_FONT, fill: 0xffffff, align: 'left', wordWrap: true },
  })
  titleText.anchor.set(0, 0)
  node.addChild(iconSprite, titleText)
  if (iconOverlay !== null) node.addChild(iconOverlay)

  const rows = descriptions.map((text) => {
    const bullet = new Graphics()
    const body = new Text({
      text,
      style: {
        fontFamily: fontFamilyForLocale(),
        fontSize: BODY_FONT,
        fill: 0xffffff,
        align: 'left',
        wordWrap: true,
        breakWords: true,
      },
    })
    body.anchor.set(0, 0)
    node.addChild(bullet, body)
    return { bullet, body }
  })

  return {
    node,
    layout(s, centerX, top) {
      const left = centerX - (CONTENT_W / 2) * s
      const textX = left + ICON_COLUMN_W * s
      const textWidth = (CONTENT_W - ICON_COLUMN_W) * s

      iconSprite.setSize(iconW * s, iconH * s)
      iconSprite.position.set(left + iconOffsetX * s, top + iconOffsetY * s)
      if (iconOverlay !== null && icon.overlay !== undefined) {
        iconOverlay.setSize(icon.overlay.width * iconScale * s, icon.overlay.height * iconScale * s)
        iconOverlay.position.set(
          left + (iconOffsetX + iconW / 2) * s,
          top + (iconOffsetY + iconH / 2) * s,
        )
      }

      titleText.style.fontSize = BODY_FONT * s
      titleText.style.wordWrapWidth = textWidth
      titleText.position.set(textX, top)

      let y = top + titleText.height
      for (const { bullet, body } of rows) {
        y += ROW_GAP * s
        body.style.fontSize = BODY_FONT * s
        body.style.wordWrapWidth = textWidth
        body.position.set(textX, y)
        const size = BULLET_SIZE * s
        bullet
          .clear()
          .rect(left + BULLET_X * s, y + BULLET_CY * s - size / 2, size, size)
          .fill({ color: 0xffffff })
        y += body.height
      }
      return y - top
    },
  }
}

/** 把多個積木包成一組（Unity 的 WildSymbols 這類巢狀 VerticalLayoutGroup），以自己的 spacing 排列。 */
export function groupBlock(children: readonly InfoBlock[], spacing: number): InfoBlock {
  const node = new Container()
  for (const child of children) node.addChild(child.node)
  return {
    node,
    layout(s, centerX, top) {
      let y = top
      children.forEach((child, index) => {
        if (index > 0) y += spacing * s
        y += child.layout(s, centerX, y)
      })
      return y - top
    },
  }
}
